import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import EmailCapture from './EmailCapture';
import './App.css';

const MANIFEST_URL = '/podcast-content/_index.json';
const CONTENT_BASE = '/podcast-content/episodes/';

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

const PodcastEpisodePage = () => {
  const { slug } = useParams();
  const [manifest, setManifest] = useState(null);
  const [body, setBody] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    (async () => {
      try {
        const mRes = await fetch(MANIFEST_URL, { cache: 'no-cache' });
        if (!mRes.ok) throw new Error(`Manifest fetch failed (${mRes.status})`);
        const m = await mRes.json();
        if (cancelled) return;
        setManifest(m);

        const ep = (m.episodes || []).find((e) => e.slug === slug);
        if (!ep) throw new Error('Episode not found.');

        const fileURL = `${CONTENT_BASE}${ep.notesFile || (ep.slug + '.md')}`;
        const r = await fetch(fileURL, { cache: 'no-cache' });
        if (!r.ok) {
          // Notes are optional; render audio + summary even if no notes file exists.
          if (!cancelled) setBody('');
          return;
        }
        const text = await r.text();
        if (!cancelled) setBody(text);
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const ep = useMemo(
    () => (manifest?.episodes || []).find((e) => e.slug === slug),
    [manifest, slug]
  );

  useEffect(() => {
    if (ep?.title) {
      const previous = document.title;
      document.title = `${ep.title} — Made in mChatAI`;
      return () => { document.title = previous; };
    }
  }, [ep]);

  const markdownComponents = useMemo(() => ({
    h1: ({ children }) => (
      <h2 style={{ fontSize: '1.8rem', fontWeight: 800, color: '#111827', margin: '0 0 16px' }}>{children}</h2>
    ),
    h2: ({ children }) => (
      <h3 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827', margin: '28px 0 10px' }}>{children}</h3>
    ),
    p: ({ children }) => (
      <p style={{ color: '#374151', lineHeight: 1.7, marginBottom: 14 }}>{children}</p>
    ),
    ul: ({ children }) => (
      <ul style={{ color: '#374151', lineHeight: 1.8, marginBottom: 14, paddingLeft: 22 }}>{children}</ul>
    ),
    a: ({ href, children, ...rest }) => (
      <a href={href} style={{ color: '#3b82f6' }} {...rest}>{children}</a>
    ),
  }), []);

  return (
    <div className="mp-page" style={{ background: '#fff', minHeight: 'calc(100vh - 60px)' }}>
      <article style={{ maxWidth: 720, margin: '0 auto', padding: '64px 24px 32px' }}>
        <div style={{ marginBottom: 12, fontSize: '0.9rem' }}>
          <Link to="/podcast" style={{ color: '#3b82f6', textDecoration: 'none' }}>← All episodes</Link>
        </div>

        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
            padding: 16, borderRadius: 8, marginBottom: 24
          }}>
            {error}
          </div>
        )}

        {ep && (
          <>
            <header style={{ marginBottom: 24 }}>
              <div style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: 8 }}>
                {ep.episodeNumber && <span style={{ fontWeight: 600, color: '#111827' }}>Ep {ep.episodeNumber}</span>}
                {ep.episodeNumber && <span> · </span>}
                <span>{formatDate(ep.publishedAt)}</span>
              </div>
              <h1 style={{ fontSize: '2.2rem', fontWeight: 800, color: '#111827', margin: '0 0 12px', lineHeight: 1.15 }}>
                {ep.title}
              </h1>
              <p style={{ color: '#4b5563', lineHeight: 1.6, margin: '0 0 16px' }}>
                {ep.summary}
              </p>
              {ep.audioURL && (
                <audio controls preload="metadata" style={{ width: '100%' }}>
                  <source src={ep.audioURL} type={ep.audioMimeType || 'audio/mpeg'} />
                  Your browser does not support the audio element.
                </audio>
              )}
            </header>

            {body ? (
              <section>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {body}
                </ReactMarkdown>
              </section>
            ) : null}
          </>
        )}

        {!ep && !error && (
          <p style={{ color: '#6b7280' }}>Loading…</p>
        )}
      </article>

      <section style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 96px' }}>
        <EmailCapture
          source={`podcast-episode:${slug}`}
          title="Don’t miss the next episode"
          subtitle="One email each Friday with the new episode + show notes."
        />
      </section>
    </div>
  );
};

export default PodcastEpisodePage;
